import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPhone, FiAward } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const images = [
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=2075&q=80",
  "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=2075&q=80",
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2075&q=80"
];

const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="home" className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Background Image Slider with Crossfade & Overlay */}
      <div className="absolute inset-0 z-0 bg-navy">
        <AnimatePresence>
          <motion.img 
            key={currentImageIndex}
            src={images[currentImageIndex]} 
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
            alt="Real estate in Texas" 
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/80 to-navy/40 z-10 pointer-events-none"></div>
      </div>

      <div className="container-custom relative z-20 w-full pt-20 pb-16" style={{ perspective: "1000px" }}>
        
        {/* Continuous 3D floating effect for text content */}
        <motion.div 
          animate={{ 
            rotateX: [0, 2, 0], 
            rotateY: [0, -2, 0],
            y: [0, -10, 0]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="max-w-3xl"
          style={{ transformStyle: "preserve-3d" }}
        >
          <motion.div 
            style={{ transform: "translateZ(40px)" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-medium mb-6"
          >
            <SafeIcon icon={FiAward} className="text-gold w-4 h-4" />
            <span>Certified Appraisers | Texas Licensed</span>
          </motion.div>

          <motion.h1 
            style={{ transform: "translateZ(60px)" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
          >
            Where Experience Meets Excellence in <span className="text-gold">Property Appraisal</span>
          </motion.h1>

          <motion.p 
            style={{ transform: "translateZ(40px)" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-200 mb-10 max-w-2xl leading-relaxed"
          >
            Credible, Timely Appraisals for Residential & Commercial Properties Across Texas.
          </motion.p>

          <motion.div 
            style={{ transform: "translateZ(50px)" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a href="#contact" className="btn-primary text-lg">
              Request Your Appraisal
            </a>
            <a href="tel:+12148854504" className="btn-secondary bg-transparent border-white/30 text-white hover:bg-white hover:text-navy text-lg flex items-center gap-2">
              <SafeIcon icon={FiPhone} />
              Talk to an Expert
            </a>
          </motion.div>

          <motion.div 
            style={{ transform: "translateZ(30px)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="mt-12 flex items-center gap-4 text-white/80"
          >
            <div className="h-[1px] w-12 bg-gold"></div>
            <p className="font-medium">Call directly: <a href="tel:+12148854504" className="text-white hover:text-gold transition-colors font-bold tracking-wide">+1-214-885-4504</a></p>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Slider Controls */}
      <div className="absolute bottom-10 left-0 right-0 flex justify-center gap-3 z-30">
        {images.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentImageIndex(idx)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              currentImageIndex === idx ? 'bg-gold scale-125' : 'bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;